/**
 * BetDetail helpers — mirrors BetDetailInfoScreen (mobile) derivations.
 * Source of truth: published bet object from GET /api/bets/published.
 */

import { STATUS_DONE, STATUS_LIVE } from "../components/bondhouse/FixtureStatus";
import { formatAppDate, formatAppDateTime, formatAppTime } from "./appTimeFormat";

const num = (v) => Number(v) || 0;

export const fmtCompact = (n) =>
  new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(
    num(n)
  );

export const fmtFull = (n) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(num(n)));

export const fmtCash = (n) => `${fmtFull(n)} Cash`;

export const formatLastUpdated = (date) => {
  if (!date) return null;
  const updated = new Date(date).getTime();
  if (Number.isNaN(updated)) return null;
  const diffMs = Date.now() - updated;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMins > 0) return `${diffMins}m ago`;
  return "Just now";
};

/* ─────────── Header stats (Pool · Pending · Players · Followers) ─────────── */

export const getHeaderStats = (house) => {
  const betAmounts = Array.isArray(house?.betAmounts) ? house.betAmounts : [];
  const uniqueBettors = new Set(
    betAmounts.map((e) => e.user?.toString?.() ?? e.user).filter(Boolean)
  ).size;

  return {
    pool: num(house?.amountAvailable),
    pending: num(house?.pendingBalance),
    players: uniqueBettors,
    followers: Array.isArray(house?.followers) ? house.followers.length : 0,
  };
};

export const isLowLiquidity = (house) => {
  const pool = num(house?.amountAvailable);
  const thresh = num(house?.lowBalanceThreshold);
  return thresh > 0 && pool > 0 && pool <= thresh;
};

export const isFollowingHouse = (house, userId) => {
  if (!house || !userId) return false;
  const followers = Array.isArray(house?.followers) ? house.followers : [];
  return followers.some((f) => String(f.user?._id || f.user) === String(userId));
};

/* ─────────── House pulse ─────────── */

export const getHousePulse = (house) => {
  const betAmounts = Array.isArray(house?.betAmounts) ? house.betAmounts : [];
  const viewers = Array.isArray(house?.viewers) ? house.viewers : [];
  const followers = Array.isArray(house?.followers) ? house.followers : [];

  const totalTickets = betAmounts.length;
  const uniqueBettors = new Set(
    betAmounts.map((e) => e.user?.toString?.() ?? e.user).filter(Boolean)
  ).size;
  const betVolume = betAmounts.reduce((s, e) => s + num(e.amount), 0);
  const avgTicketSize = totalTickets > 0 ? Math.round(betVolume / totalTickets) : 0;

  const viewsCount = viewers.length;
  const followersCount = followers.length;
  const reachMax = Math.max(viewsCount, followersCount, 1);

  const status = house?.status || "active";
  const statusLabel =
    status.length > 0 ? status.charAt(0).toUpperCase() + status.slice(1) : "Active";

  return {
    status,
    statusLabel,
    isPaused: status === "paused",
    betVolume,
    totalTickets,
    uniqueBettors,
    avgTicketSize,
    viewsCount,
    followersCount,
    viewsBarPct: Math.round((viewsCount / reachMax) * 100),
    followersBarPct: Math.round((followersCount / reachMax) * 100),
  };
};

/* ─────────── Betting analytics (last 10 stakes chart) ─────────── */

export const getBettingAnalytics = (house) => {
  const betAmounts = Array.isArray(house?.betAmounts) ? house.betAmounts : [];
  if (!betAmounts.length) return null;

  const amounts = betAmounts.map((e) => num(e.amount)).filter((a) => a > 0);
  if (!amounts.length) return null;

  const total = amounts.reduce((s, a) => s + a, 0);
  const avg = total / amounts.length;
  const highest = Math.max(...amounts);
  const lowest = Math.min(...amounts);

  const sortedEntries = [...betAmounts].sort((a, b) => {
    const dateA = new Date(a.placedAt || a.createdAt || a.updatedAt || 0).getTime();
    const dateB = new Date(b.placedAt || b.createdAt || b.updatedAt || 0).getTime();
    return dateA - dateB;
  });

  const recent = sortedEntries.slice(-10);
  const startIndex = Math.max(1, sortedEntries.length - 9);
  const recentBets = recent.map((entry, idx) => ({
    key: `${entry._id || idx}`,
    amount: num(entry.amount),
    label: `#${startIndex + idx}`,
  }));

  const lastBet = sortedEntries[sortedEntries.length - 1];
  const lastBetTime = lastBet
    ? new Date(lastBet.placedAt || lastBet.createdAt || lastBet.updatedAt || 0)
    : null;

  return { total, avg, highest, lowest, count: amounts.length, recentBets, lastBetTime };
};

/* ─────────── Payout mix ─────────── */

export const getPayoutMix = (house) => {
  const wins = Array.isArray(house?.wins) ? house.wins : [];
  const losses = Array.isArray(house?.losses) ? house.losses : [];
  const winAmount = wins.reduce((s, e) => s + num(e.amount), 0);
  const lossAmount = losses.reduce((s, e) => s + num(e.amount), 0);
  const houseProfit = lossAmount - winAmount;
  const total = winAmount + lossAmount;
  return {
    winAmount,
    lossAmount,
    houseProfit,
    winPct: total > 0 ? (winAmount / total) * 100 : 0,
    lossPct: total > 0 ? (lossAmount / total) * 100 : 0,
    hasData: winAmount > 0 || lossAmount > 0,
  };
};

/* ─────────── About house ─────────── */

export const getAboutHouse = (house) => {
  const tags = Array.isArray(house?.tags)
    ? house.tags.filter((t) => typeof t === "string" && t.trim()).map((t) => t.trim())
    : [];

  const visibilityMode = house?.visibility === "followers" ? "followers" : "public";

  const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : formatAppDate(d);
  };

  return {
    tags,
    visibilityMode,
    houseSince: formatDate(house?.createdAt),
    houseClosedAt:
      house?.status === "closed" ? formatDate(house?.completedAt) : null,
    amountAllocated: num(house?.amountAllocated),
    brandStory: typeof house?.brandStory === "string" ? house.brandStory.trim() : "",
  };
};

/* ─────────── Stakes & limits ─────────── */

export const getStakeRows = (house, fixtureCount) => {
  const minBetAmount = num(house?.defaultMinBetAmount || house?.minBetAmount);
  const maxBetVal = num(house?.defaultMaxBetAmount || house?.maxBetAmount);
  const maxTicketPayoutVal = num(house?.maxTicketPayout);
  const minGamesVal = num(house?.minSelectionsPerBet || house?.minFixture || 2);
  const maxGamesVal = num(house?.maxFixture || fixtureCount);

  const rows = [
    {
      key: "minBet",
      label: "Minimum bet",
      shortLabel: "Min bet",
      kind: "cash",
      amount: minBetAmount,
      value: fmtCash(minBetAmount),
      hint: house?.defaultMinBetAmountLastUpdatedAt
        ? `Updated ${formatLastUpdated(house.defaultMinBetAmountLastUpdatedAt)}`
        : null,
    },
    {
      key: "maxBet",
      label: "Maximum bet",
      shortLabel: "Max bet",
      kind: "cash",
      amount: maxBetVal,
      value: fmtCash(maxBetVal),
      hint: house?.defaultMaxBetAmountLastUpdatedAt
        ? `Updated ${formatLastUpdated(house.defaultMaxBetAmountLastUpdatedAt)}`
        : null,
    },
  ];

  if (maxTicketPayoutVal > 0) {
    rows.push({
      key: "maxTicketPayout",
      label: "Max payout per ticket",
      shortLabel: "Max payout",
      kind: "cash",
      amount: maxTicketPayoutVal,
      value: fmtCash(maxTicketPayoutVal),
      hint: "Cap on winnings for a single ticket on this house",
    });
  }

  rows.push(
    {
      key: "minGames",
      label: "Minimum picks per ticket",
      shortLabel: "Min picks",
      kind: "count",
      amount: minGamesVal,
      value: String(minGamesVal),
      hint: house?.updatedAt ? `House info ${formatLastUpdated(house.updatedAt)}` : null,
    },
    {
      key: "maxGames",
      label: "Games on board",
      shortLabel: "Games",
      kind: "count",
      amount: maxGamesVal,
      value: String(maxGamesVal),
      hint: null,
    }
  );

  return rows;
};

/* ─────────── Next kickoff countdown ─────────── */

export const getNextKickoff = (fixtures = []) => {
  const now = Date.now();
  const withDates = fixtures.filter((f) => {
    if (!f?.date) return false;
    return !Number.isNaN(new Date(f.date).getTime());
  });
  if (!withDates.length) return null;

  const sorted = [...withDates].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  const next = sorted.find((f) => new Date(f.date).getTime() > now);
  if (!next) return null;

  const diff = new Date(next.date).getTime() - now;
  if (diff <= 0) return null;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { fixture: next, parts: { days, hours, minutes, seconds } };
};

export const formatCountdownCompact = ({ days, hours, minutes, seconds }) => {
  const h = String(Math.max(0, hours)).padStart(2, "0");
  const m = String(Math.max(0, minutes)).padStart(2, "0");
  const s = String(Math.max(0, seconds)).padStart(2, "0");
  if (days > 0) return `${days}d ${h}:${m}:${s}`;
  if (hours > 0) return `${h}:${m}:${s}`;
  return `${m}:${s}`;
};

/* ─────────── Fixtures ─────────── */

export const sortFixturesForSchedule = (fixtures = []) =>
  [...fixtures].sort((a, b) => {
    const ta = a?.date ? new Date(a.date).getTime() : Number.MAX_SAFE_INTEGER;
    const tb = b?.date ? new Date(b.date).getTime() : Number.MAX_SAFE_INTEGER;
    return ta - tb;
  });

export const getFixtureScore = (fixture) => {
  const short = (fixture?.status?.short || "").toUpperCase();
  const showScore = short === "FT" || short === "HT" || STATUS_LIVE.has(short) || STATUS_DONE.has(short);
  const home =
    fixture?.score?.fulltime?.home ??
    fixture?.goals?.home ??
    null;
  const away =
    fixture?.score?.fulltime?.away ??
    fixture?.goals?.away ??
    null;
  return { showScore, home, away, short: fixture?.status?.short || "NS" };
};

export const formatFixtureKickoff = (fixture) => {
  if (!fixture?.date || Number.isNaN(new Date(fixture.date).getTime())) {
    return "Kickoff TBD";
  }
  return formatAppDateTime(fixture.date);
};

export const formatFixtureKickoffShort = (fixture) => {
  if (!fixture?.date || Number.isNaN(new Date(fixture.date).getTime())) {
    return "TBD";
  }
  const kickoff = new Date(fixture.date);
  const now = new Date();
  const sameDay = kickoff.toDateString() === now.toDateString();
  if (sameDay) {
    return formatAppTime(kickoff);
  }
  return formatAppDateTime(kickoff);
};

export const countLiveFixtures = (fixtures = []) =>
  fixtures.filter((f) => STATUS_LIVE.has((f?.status?.short || "").toUpperCase())).length;

/* ─────────── Treasury (balance lines from bet model) ─────────── */

export const getTreasuryDetails = (house) => {
  const balance = num(house?.balance);
  const available = num(house?.amountAvailable);
  const pending = num(house?.pendingBalance);
  const profitHeld = num(house?.houseProfitBalance);
  const allocated = num(house?.amountAllocated);
  const lowThreshold = num(house?.lowBalanceThreshold);
  const total = balance || available + pending;

  return {
    balance,
    available,
    pending,
    profitHeld,
    allocated,
    lowThreshold,
    total,
    availableUpdated: formatLastUpdated(house?.amountAvailableLastUpdatedAt),
    pendingUpdated: formatLastUpdated(house?.pendingBalanceLastUpdatedAt),
    profitUpdated: formatLastUpdated(house?.houseProfitBalanceLastUpdatedAt),
  };
};

/* ─────────── Board summary (fixture roll-up) ─────────── */

export const getBoardSummary = (fixtures = []) => {
  let live = 0;
  let upcoming = 0;
  let done = 0;
  let suspended = 0;
  let markets = 0;
  let boardBettors = 0;

  fixtures.forEach((f) => {
    const short = (f?.status?.short || "").toUpperCase();
    if (STATUS_LIVE.has(short)) live += 1;
    else if (STATUS_DONE.has(short)) done += 1;
    else upcoming += 1;
    if (f?.suspended) suspended += 1;
    markets += num(f?.marketsOffered);
    boardBettors += num(f?.bettorsCount);
  });

  return {
    total: fixtures.length,
    live,
    upcoming,
    done,
    suspended,
    markets,
    boardBettors,
  };
};

/* ─────────── Operators (accepted owners/admins) ─────────── */

export const getOperators = (house) => {
  const users = Array.isArray(house?.users) ? house.users : [];
  return users
    .filter((u) => !u.status || u.status === "accepted")
    .map((u) => ({
      id: u.userId?._id || u.userId,
      name: u.userId?.name || "Operator",
      role: u.role || "owner",
      joinedAt: u.joinedAt ? formatLastUpdated(u.joinedAt) : null,
    }));
};

/* ─────────── Recent stakes tape ─────────── */

export const getRecentStakes = (house, limit = 12) => {
  const betAmounts = Array.isArray(house?.betAmounts) ? house.betAmounts : [];
  if (!betAmounts.length) return [];

  const sorted = [...betAmounts].sort((a, b) => {
    const dateA = new Date(a.placedAt || a.createdAt || a.updatedAt || 0).getTime();
    const dateB = new Date(b.placedAt || b.createdAt || b.updatedAt || 0).getTime();
    return dateB - dateA;
  });

  const total = sorted.length;
  return sorted.slice(0, limit).map((entry, idx) => {
    const when = entry.placedAt || entry.createdAt || entry.updatedAt;
    return {
      key: entry._id || `stake-${idx}`,
      amount: num(entry.amount),
      label: `#${total - idx}`,
      when: when ? formatLastUpdated(when) : null,
      at: when ? new Date(when) : null,
    };
  });
};
